import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  InputAdornment,
  MenuItem,
  Menu,
  Tooltip,
  Fab
} from '@mui/material';
import {
  QuestionAnswer,
  Add,
  Edit,
  Delete,
  Search,
  ContentCopy,
  MoreVert,
  Visibility,
  VisibilityOff,
  TrendingUp
} from '@mui/icons-material';
import api from '../services/api';
import { PermissionGuard } from '../components/Permissions/PermissionGuard'; // 14/11/2025 - User Access Management Module Phase 5.2.3

interface CannedResponse {
  id: number;
  short_code: string;
  content: string;
  is_private: boolean;
  usage_count: number;
  user_id: number;
  created_at: string;
}

const CannedResponsesPage: React.FC = () => {
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includePrivate, setIncludePrivate] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingResponse, setEditingResponse] = useState<CannedResponse | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedResponse, setSelectedResponse] = useState<CannedResponse | null>(null);
  
  const [formData, setFormData] = useState({
    short_code: '',
    content: '',
    is_private: false
  });

  useEffect(() => {
    fetchCannedResponses();
  }, [searchTerm, includePrivate]);

  const fetchCannedResponses = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('include_private', includePrivate.toString());
      
      const response = await api.get(`/canned-responses/?${params}`);
      setCannedResponses(response.data);
    } catch (error: any) {
      setError('Failed to fetch canned responses');
      console.error('Error fetching canned responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      short_code: '',
      content: '',
      is_private: false
    });
    setEditingResponse(null);
  };

  const handleOpenDialog = (response?: CannedResponse) => {
    if (response) {
      setEditingResponse(response);
      setFormData({
        short_code: response.short_code,
        content: response.content,
        is_private: response.is_private
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!formData.short_code.trim() || !formData.content.trim()) {
      setError('Short code and content are required');
      return;
    }

    try {
      if (editingResponse) {
        await api.put(`/canned-responses/${editingResponse.id}`, formData);
        setSuccess('Canned response updated successfully');
      } else {
        await api.post('/canned-responses/', formData);
        setSuccess('Canned response created successfully');
      }
      
      handleCloseDialog();
      fetchCannedResponses();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to save canned response';
      setError(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this canned response?')) return;

    try {
      await api.delete(`/canned-responses/${id}`);
      setSuccess('Canned response deleted successfully');
      fetchCannedResponses();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError('Failed to delete canned response');
    }
  };

  const handleUse = async (response: CannedResponse) => {
    try {
      await api.post(`/canned-responses/${response.id}/use`);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(response.content);
      setSuccess('Content copied to clipboard and usage tracked');
      
      // Update local usage count
      setCannedResponses(prev => prev.map(r => 
        r.id === response.id ? { ...r, usage_count: r.usage_count + 1 } : r
      ));
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      // Fallback to just copying to clipboard
      try {
        await navigator.clipboard.writeText(response.content);
        setSuccess('Content copied to clipboard');
        setTimeout(() => setSuccess(null), 3000);
      } catch (clipboardError) {
        setError('Failed to copy to clipboard');
      }
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, response: CannedResponse) => {
    setAnchorEl(event.currentTarget);
    setSelectedResponse(response);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedResponse(null);
  };

  const filteredResponses = cannedResponses.filter(response =>
    response.short_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    response.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatShortCode = (shortCode: string) => {
    return shortCode.startsWith('!') ? shortCode : `!${shortCode}`;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom display="flex" alignItems="center" gap={1}>
        <QuestionAnswer />
        Canned Responses
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Header Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manage Quick Responses
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create reusable message templates to speed up your customer support. 
          Use short codes like "!welcome" or "!thanks" to quickly insert responses.
        </Typography>

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search responses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: '300px' }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={includePrivate}
                onChange={(e) => setIncludePrivate(e.target.checked)}
              />
            }
            label="Include private responses"
          />

          <PermissionGuard permission="canned_responses:create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Response
            </Button>
          </PermissionGuard>
        </Box>
      </Paper>

      {/* Content Section */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredResponses.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <QuestionAnswer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {searchTerm ? 'No matching responses found' : 'No canned responses yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Create your first canned response to get started with quick replies'
            }
          </Typography>
          {!searchTerm && (
            <PermissionGuard permission="canned_responses:create">
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Create First Response
              </Button>
            </PermissionGuard>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredResponses.map((response) => (
            <Grid item xs={12} md={6} lg={4} key={response.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: (theme) => theme.shadows[4]
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={formatShortCode(response.short_code)}
                        color="primary"
                        size="small"
                        sx={{ fontFamily: 'monospace' }}
                      />
                      {response.is_private && (
                        <Tooltip title="Private response">
                          <VisibilityOff sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Tooltip>
                      )}
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, response)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      minHeight: '60px'
                    }}
                  >
                    {response.content}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <TrendingUp sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Used {response.usage_count} times
                      </Typography>
                    </Box>
                    
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => handleUse(response)}
                    >
                      Use
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedResponse) handleOpenDialog(selectedResponse);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedResponse) handleUse(selectedResponse);
          handleMenuClose();
        }}>
          <ContentCopy sx={{ mr: 1 }} />
          Copy & Use
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedResponse) handleDelete(selectedResponse.id);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingResponse ? 'Edit Canned Response' : 'Create New Canned Response'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Short Code"
              value={formData.short_code}
              onChange={(e) => setFormData({ ...formData, short_code: e.target.value })}
              placeholder="welcome, thanks, pricing..."
              fullWidth
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">!</InputAdornment>,
              }}
              helperText="Enter a short memorable code to quickly access this response"
            />
            
            <TextField
              label="Response Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              multiline
              rows={6}
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Write your response message here..."
              helperText={`${formData.content.length} characters`}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_private}
                  onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                />
              }
              label="Private (only visible to you)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.short_code.trim() || !formData.content.trim()}
          >
            {editingResponse ? 'Update' : 'Create'} Response
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'block', md: 'none' }
        }}
        onClick={() => handleOpenDialog()}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default CannedResponsesPage;